// Shared visual tokens + small layout components for the RPA module.
//
// The Finance Portal already has a green accent (#22c55e) — the RPA pages
// use a slate base + the same accent + indigo for "AI / robot" context so
// the screens read as their own coherent surface inside the portal.
//
// Everything here is presentation-only. Components stay dumb on data shape.
import React from 'react';
import { Box, Paper, Stack, Typography, Chip, alpha, useTheme } from '@mui/material';

export const RPA_COLORS = {
    // Primary accent — green to match Finance Portal sidebar
    accent:        '#22c55e',
    accentSoft:    'rgba(34, 197, 94, 0.10)',
    accentBorder:  'rgba(34, 197, 94, 0.35)',
    // Robot / AI context — indigo
    robot:         '#6366f1',
    robotSoft:     'rgba(99, 102, 241, 0.10)',
    // Statuses
    success:       '#16a34a',
    warning:       '#f59e0b',
    danger:        '#ef4444',
    info:          '#0ea5e9',
    neutral:       '#64748b',
    // Surfaces
    surface:       '#ffffff',
    surfaceAlt:    '#f8fafc',
    surfaceMuted:  '#f1f5f9',
    border:        'rgba(15, 23, 42, 0.08)',
    // Text
    text:          '#0f172a',
    textMuted:     '#64748b',
    textFaint:     '#94a3b8',
};

export const RPA_FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Status → color mapping (single source of truth across all RPA components).
export const STATUS_TONE = {
    queued:        { color: 'default',  label: 'Queued' },
    running:       { color: 'info',     label: 'Running' },
    succeeded:     { color: 'success',  label: 'Succeeded' },
    failed:        { color: 'error',    label: 'Failed' },
    rerun:         { color: 'info',     label: 'Rerun' },
    cancelled:     { color: 'default',  label: 'Cancelled' },
    // Legacy aliases — pre-migration rows render a chip instead of
    // falling through to the unknown-status default.
    error:         { color: 'error',    label: 'Failed' },
    needs_repair:  { color: 'error',    label: 'Failed' },
    // Document statuses
    extracted:     { color: 'success',  label: 'Extracted' },
    needs_review:  { color: 'warning',  label: 'Needs review' },
    extract_failed:{ color: 'error',    label: 'Extract failed' },
    corrected:     { color: 'info',     label: 'Corrected' },
    superseded:    { color: 'default',  label: 'Superseded' },
};


/**
 * StatusChip — small, consistent status indicator used across all RPA tables.
 * Falls back to a neutral chip if the status is unknown.
 */
export function StatusChip({ status, size = 'small', sx }) {
    if (!status) return null;
    const tone = STATUS_TONE[String(status).toLowerCase()] || { color: 'default', label: status };
    return (
        <Chip
            size={size}
            color={tone.color}
            label={tone.label}
            sx={{
                fontWeight: 600,
                letterSpacing: 0.2,
                textTransform: 'none',
                ...sx,
            }}
        />
    );
}


/**
 * KpiCard — top-of-tab summary tile.
 *
 *   <KpiGrid>
 *     <KpiCard label="Active merchants" value={12} hint="2 added this week" />
 *     <KpiCard label="Workflows"        value={5}  trend={+1} />
 *   </KpiGrid>
 */
export function KpiCard({ label, value, hint, trend, icon, accent, onClick }) {
    const isClickable = typeof onClick === 'function';
    const accentColor = accent || RPA_COLORS.accent;
    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                px: 2.25,
                py: 1.75,
                borderRadius: 2,
                border: `1px solid ${RPA_COLORS.border}`,
                background: `linear-gradient(180deg, ${RPA_COLORS.surface} 0%, ${RPA_COLORS.surfaceAlt} 100%)`,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'transform 120ms, box-shadow 120ms, border-color 120ms',
                '&:hover': isClickable ? {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
                    borderColor: accentColor,
                } : undefined,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 92,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                {icon && (
                    <Box sx={{
                        width: 28, height: 28, borderRadius: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: alpha(accentColor, 0.10),
                        color: accentColor,
                    }}>
                        {icon}
                    </Box>
                )}
                <Typography variant="caption" sx={{
                    color: RPA_COLORS.textMuted,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    fontSize: 11,
                }}>
                    {label}
                </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography sx={{
                    fontFamily: RPA_FONT,
                    fontWeight: 700,
                    fontSize: 28,
                    lineHeight: 1.1,
                    color: RPA_COLORS.text,
                }}>
                    {value}
                </Typography>
                {trend !== undefined && trend !== 0 && (
                    <Chip
                        size="small"
                        label={`${trend > 0 ? '+' : ''}${trend}`}
                        sx={{
                            height: 18,
                            fontSize: 11,
                            fontWeight: 600,
                            color: trend > 0 ? RPA_COLORS.success : RPA_COLORS.danger,
                            bgcolor: alpha(trend > 0 ? RPA_COLORS.success : RPA_COLORS.danger, 0.12),
                        }}
                    />
                )}
            </Stack>
            {hint && (
                <Typography variant="caption" sx={{ color: RPA_COLORS.textFaint, mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
        </Paper>
    );
}


/**
 * KpiGrid — responsive 4-up grid of KpiCard. Wraps to 2-up on small screens.
 */
export function KpiGrid({ children }) {
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
            },
            gap: 1.5,
            mb: 2.5,
        }}>
            {children}
        </Box>
    );
}


/**
 * SectionPaper — the standard surface for tables and content blocks.
 * Subtle border, rounded corners, no shadow until hover.
 */
export function SectionPaper({ children, sx, ...rest }) {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${RPA_COLORS.border}`,
                overflow: 'hidden',
                ...sx,
            }}
            {...rest}
        >
            {children}
        </Paper>
    );
}


/**
 * TableShell — wraps a <Table> with sticky header + zebra rows + tight density.
 * Use as <TableShell><Table>...</Table></TableShell>.
 */
export function TableShell({ children, sx }) {
    return (
        <Box sx={{
            // Sticky header
            '& thead th': {
                background: RPA_COLORS.surfaceMuted,
                color: RPA_COLORS.textMuted,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                borderBottom: `1px solid ${RPA_COLORS.border}`,
                position: 'sticky',
                top: 0,
                zIndex: 1,
            },
            '& tbody tr': {
                transition: 'background-color 120ms',
            },
            '& tbody tr:nth-of-type(odd)': {
                backgroundColor: alpha(RPA_COLORS.surfaceAlt, 0.5),
            },
            '& tbody tr:hover': {
                backgroundColor: alpha(RPA_COLORS.accent, 0.04),
            },
            '& tbody td': {
                fontSize: 13,
                color: RPA_COLORS.text,
                borderBottom: `1px solid ${alpha(RPA_COLORS.border, 0.5)}`,
            },
            ...sx,
        }}>
            {children}
        </Box>
    );
}


/**
 * EmptyState — large, friendly empty placeholder with icon + CTA.
 *
 *   <EmptyState
 *     icon={<RobotIcon />}
 *     title="No merchants yet"
 *     message="Add your first merchant portal to start training the agent."
 *     action={<Button variant="contained">Add merchant</Button>}
 *   />
 */
export function EmptyState({ icon, title, message, action }) {
    return (
        <Box sx={{
            py: 6, px: 4,
            textAlign: 'center',
            background: RPA_COLORS.surfaceAlt,
            borderRadius: 2,
        }}>
            {icon && (
                <Box sx={{
                    width: 56, height: 56, mx: 'auto', mb: 2,
                    borderRadius: '50%',
                    background: alpha(RPA_COLORS.robot, 0.12),
                    color: RPA_COLORS.robot,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {icon}
                </Box>
            )}
            <Typography variant="h6" sx={{
                fontFamily: RPA_FONT,
                fontWeight: 700,
                color: RPA_COLORS.text,
                mb: 0.5,
            }}>
                {title}
            </Typography>
            {message && (
                <Typography variant="body2" sx={{
                    color: RPA_COLORS.textMuted,
                    maxWidth: 480,
                    mx: 'auto',
                    mb: action ? 2.5 : 0,
                }}>
                    {message}
                </Typography>
            )}
            {action}
        </Box>
    );
}


/**
 * Toolbar — table action bar with consistent spacing and a thin bottom border.
 */
export function Toolbar({ children, sx }) {
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="center"
               sx={{
                   px: 1.5, py: 1.25,
                   borderBottom: `1px solid ${RPA_COLORS.border}`,
                   background: RPA_COLORS.surface,
                   ...sx,
               }}>
            {children}
        </Stack>
    );
}


/**
 * SmallStat — inline label + value used inside table cells / detail dialogs.
 */
export function SmallStat({ label, value }) {
    return (
        <Stack spacing={0.25}>
            <Typography variant="caption" sx={{
                color: RPA_COLORS.textFaint,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                fontSize: 10,
            }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color: RPA_COLORS.text, fontWeight: 500 }}>
                {value ?? '—'}
            </Typography>
        </Stack>
    );
}


// Helper for displaying ISO dates compactly.
export function formatDateShort(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export function formatDateLong(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export function formatBytes(bytes) {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function formatMoney(amount, currency = 'USD') {
    if (amount == null) return '—';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${currency} ${Number(amount).toFixed(2)}`;
    }
}
